// path: src/Components/TutorialDialog.js
import React, { useEffect, useMemo, useState } from "react";
import characters from "../Characters.json";
import { getCardImageSrc } from "../utils/cardImages";
import {
    isAnxiousModeUnlocked,
    isBlitzModeUnlocked,
    isShyCorgisUnlocked,
    isTwistedModeUnlocked,
} from "../utils/progression";
import { getUnlockRequirementText } from "../utils/storyProgress";

const BASIC_PICK_COUNT = 4;
const ADVANCED_CARDS_COUNT = 4;
const SHY_TUTORIAL_DEMO_INTERVAL_MS = 2000;

const getTutorialCards = (count) => characters.slice(0, count);

const getImgSrc = (card) => getCardImageSrc(card);

const shuffleCards = (cards) => {
    const next = [...cards];

    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
    }

    return next;
};

const TWISTED_ROTATIONS = [0, 90, 180, 270];

const getRandomTwistedRotation = () => {
    return TWISTED_ROTATIONS[
        Math.floor(Math.random() * TWISTED_ROTATIONS.length)
    ];
};

const rotateCards = (cards) => {
    return cards.map((card) => ({
        ...card,
        tutorialRotation: getRandomTwistedRotation(),
    }));
};

const EMOJI = {
    cyclone: "\u{1F300}",
    seeNoEvilMonkey: "\u{1F648}",
    checkMark: "\u2705",
    partyPopper: "\u{1F389}",
    trophy: "\u{1F3C6}",
    playingCard: "\u{1F0CF}",
    sparkles: "\u2728",
    directHit: "\u{1F3AF}",
    brain: "\u{1F9E0}",
};

const Emoji = ({ label, symbol }) => (
    <span role="img" aria-label={label}>
        {symbol}
    </span>
);

const TutorialDialog = ({
    open,
    tutorialType = "menu",
    onClose,
    onComplete,
    variant = "modal",
}) => {
    const [activeTutorial, setActiveTutorial] = useState(tutorialType);
    const [cards, setCards] = useState(getTutorialCards(BASIC_PICK_COUNT));
    const [pickedIds, setPickedIds] = useState([]);
    const [message, setMessage] = useState("Pick any card to start.");
    const [warningCardId, setWarningCardId] = useState(null);
    const [shyDemoHiddenIds, setShyDemoHiddenIds] = useState([]);

    const twistedUnlocked = isTwistedModeUnlocked();
    const shyCorgisUnlocked = isShyCorgisUnlocked();
    const anxiousUnlocked = isAnxiousModeUnlocked();
    const blitzUnlocked = isBlitzModeUnlocked();

    const isBasic = activeTutorial === "basic";
    const isPage = variant === "page";
    const isMenu = activeTutorial === "menu";
    const isAdvancedPrompt = activeTutorial === "advancedPrompt";
    const isTwisted = activeTutorial === "twisted";
    const isShy = activeTutorial === "shy";
    const isAnxious = activeTutorial === "anxious";
    const isBlitz = activeTutorial === "blitz";
    const isBoosts = activeTutorial === "boosts";
    const isPacks = activeTutorial === "packs";
    const isHardMode = activeTutorial === "hardMode";

    const completed = pickedIds.length >= BASIC_PICK_COUNT;

    const pickedSet = useMemo(() => {
        return new Set(pickedIds.map(String));
    }, [pickedIds]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setActiveTutorial(tutorialType);
        resetTutorialState(tutorialType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tutorialType]);

    useEffect(() => {
        if (!open || activeTutorial !== "shy") {
            setShyDemoHiddenIds([]);
            return undefined;
        }

        const demoCards = cards.slice(0, 2);

        if (!demoCards.length) {
            return undefined;
        }

        const interval = setInterval(() => {
            setShyDemoHiddenIds((currentHiddenIds) => {
                if (currentHiddenIds.length > 0) {
                    return [];
                }

                return demoCards.map((card) => String(card.id));
            });
        }, SHY_TUTORIAL_DEMO_INTERVAL_MS);

        return () => {
            clearInterval(interval);
            setShyDemoHiddenIds([]);
        };
    }, [open, activeTutorial, cards]);

    if (!open) {
        return null;
    }

    function resetTutorialState(nextTutorial = activeTutorial) {
        const nextCards =
            nextTutorial === "twisted" || nextTutorial === "shy"
                ? rotateCards(getTutorialCards(ADVANCED_CARDS_COUNT))
                : getTutorialCards(BASIC_PICK_COUNT);

        setCards(nextCards);
        setPickedIds([]);
        setMessage("Pick any card to start.");
        setWarningCardId(null);
        setShyDemoHiddenIds([]);
    }

    const goToMenu = () => {
        setActiveTutorial("menu");
        resetTutorialState("menu");
    };

    const openTutorial = (nextTutorial) => {
        setActiveTutorial(nextTutorial);
        resetTutorialState(nextTutorial);
    };

    const handleClose = () => {
        resetTutorialState("menu");
        onClose();
    };

    const handlePageBack = () => {
        if (isMenu) {
            handleClose();
            return;
        }

        goToMenu();
    };

    const handleComplete = () => {
        resetTutorialState("menu");
        onComplete();
    };

    const handleBasicCardClick = (card) => {
        if (completed) {
            return;
        }

        const cardId = String(card.id);

        if (pickedSet.has(cardId)) {
            setWarningCardId(cardId);
            setMessage(
                "That was the same card again. In a real run, that costs a heart — you can spend one to keep going. Pick a different card."
            );
            return;
        }

        const nextPickedIds = [...pickedIds, cardId];
        const nextPickedCount = nextPickedIds.length;

        setPickedIds(nextPickedIds);
        setWarningCardId(null);

        if (nextPickedCount === 1) {
            setMessage("Nice! The board shuffled. Now pick a different card.");
        } else if (nextPickedCount === 2) {
            setMessage("Great memory. Keep going — choose another new card.");
        } else if (nextPickedCount === 3) {
            setMessage("One more! Pick the last card you have not chosen yet.");
        } else {
            setMessage(
                "Congrats! You finished the tutorial."
            );
        }

        setCards((currentCards) => shuffleCards(currentCards));
    };

    const handleTwistedCardClick = (card) => {
        if (completed) {
            return;
        }

        const cardId = String(card.id);

        if (pickedSet.has(cardId)) {
            setWarningCardId(cardId);
            setMessage(
                "That was the same card again. In a real Twisted Mode run, that costs a heart (Hardcore ends the run). Pick a different card."
            );
            return;
        }

        const nextPickedIds = [...pickedIds, cardId];
        const nextPickedCount = nextPickedIds.length;

        setPickedIds(nextPickedIds);
        setWarningCardId(null);

        if (nextPickedCount === 1) {
            setMessage(
                "Nice! The board shuffled, and the cards rotated. Pick a different card."
            );
        } else if (nextPickedCount === 2) {
            setMessage(
                "Good memory. Keep going — the same card may look different when rotated."
            );
        } else if (nextPickedCount === 3) {
            setMessage("One more! Pick the final card you have not chosen yet.");
        } else {
            setMessage(
                "Congrats! You completed a Twisted Mode tutorial."
            );
        }

        setCards((currentCards) => rotateCards(shuffleCards(currentCards)));
    };

    const renderAdvancedUnlockPrompt = () => (
        <>
            <div className="tutorialRuleBox">
                New modes unlocked!

                <div className="advancedTutorialText">
                    Twisted Mode and Shy Corgis are now available. You can review their
                    help from the Help section before trying them in a run.
                </div>
            </div>

            <div className="tutorialTips">
                <div>
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> Twisted Mode rotates cards after shuffles.
                </div>
                <div>
                    <Emoji label="See no evil monkey" symbol={EMOJI.seeNoEvilMonkey} /> Shy Corgis makes cards temporarily flip face-down.
                </div>
                <div>
                    <Emoji label="Check mark" symbol={EMOJI.checkMark} /> Both modes can be toggled before choosing difficulty.
                </div>
            </div>

            <div className="tutorialActions">
                <button
                    type="button"
                    className="tutorialSecondaryButton"
                    onClick={handleClose}
                >
                    Later
                </button>

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={goToMenu}
                >
                    View Help
                </button>
            </div>
        </>
    );

    const renderTutorialMenu = () => (
        <>
            <div className="tutorialRuleBox">
                Helpful tips. Story Mode teaches these mechanics through
                play. Advanced help entries unlock as you clear Story levels.
            </div>

            <div className="tutorialMenu">
                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("basic")}
                >
                    <span className="tutorialMenuButton__title">
                        Basics
                    </span>
                    <span className="tutorialMenuButton__description">
                        Refresh the core rule: pick each card once while the board shuffles.
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("twisted")}
                    disabled={!twistedUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Twisted Mode
                    </span>
                    <span className="tutorialMenuButton__description">
                        Refresh how rotated cards work.
                        {!twistedUnlocked &&
                            ` ${getUnlockRequirementText("twistedMode")}`}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("shy")}
                    disabled={!shyCorgisUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Shy Corgis
                    </span>
                    <span className="tutorialMenuButton__description">
                        Refresh how cards temporarily flip face-down.
                        {!shyCorgisUnlocked &&
                            ` ${getUnlockRequirementText("shyCorgisMode")}`}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("anxious")}
                    disabled={!anxiousUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Anxious
                    </span>
                    <span className="tutorialMenuButton__description">
                        Refresh how the board scrambles on its own.
                        {!anxiousUnlocked &&
                            ` ${getUnlockRequirementText("anxiousMode")}`}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("blitz")}
                    disabled={!blitzUnlocked}
                >
                    <span className="tutorialMenuButton__title">
                        Blitz
                    </span>
                    <span className="tutorialMenuButton__description">
                        Refresh how the 2-minute timer and forced Hardcore rules work.
                        {!blitzUnlocked &&
                            ` ${getUnlockRequirementText("blitzMode")}`}
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("boosts")}
                >
                    <span className="tutorialMenuButton__title">
                        Boosts
                    </span>
                    <span className="tutorialMenuButton__description">
                        Reveal, Shield, and Freeze — spend from Inventory during
                        Story and Endless (not Hardcore/Blitz).
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("packs")}
                >
                    <span className="tutorialMenuButton__title">
                        Corgi Packs
                    </span>
                    <span className="tutorialMenuButton__description">
                        How packs work in Inventory, Story rewards, and the Store.
                    </span>
                </button>

                <button
                    type="button"
                    className="tutorialMenuButton"
                    onClick={() => openTutorial("hardMode")}
                >
                    <span className="tutorialMenuButton__title">
                        Hard Mode
                    </span>
                    <span className="tutorialMenuButton__description">
                        Story Hard Mode and Endless Hardcore rules.
                    </span>
                </button>
            </div>

            {!isPage ? (
                <div className="tutorialActions">
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            ) : null}
        </>
    );

    const renderBasicTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Click each card only once. After every correct pick, the board
                shuffles. Picking the same card twice costs a heart — spend one
                to continue, or end the run if you are out.
            </div>

            <div className="tutorialProgress">
                Practice picks: {pickedIds.length}/{BASIC_PICK_COUNT}
            </div>

            <div className="tutorialBoard" aria-label="Tutorial cards">
                {cards.map((card) => {
                    const cardId = String(card.id);
                    const warned = warningCardId === cardId;

                    return (
                        <button
                            type="button"
                            key={card.id}
                            className={`tutorialCard tutorialCard--image ${
                                warned ? "tutorialCard--warning" : ""
                            }`}
                            onClick={() => handleBasicCardClick(card)}
                        >
                            <img
                                src={getImgSrc(card)}
                                alt={card.name}
                                className="tutorialCardImage"
                                draggable="false"
                            />
                        </button>
                    );
                })}
            </div>

            <div
                className={`tutorialMessage ${
                    completed ? "tutorialMessage--success" : ""
                }`}
                role="status"
                aria-live="polite"
            >
                {message}
            </div>

            {completed && (
                <div className="tutorialCongrats">
                    <Emoji label="Party popper" symbol={EMOJI.partyPopper} /> You are ready for a real run!
                </div>
            )}

            <div className="tutorialTips">
                <div>
                    <Emoji label="Trophy" symbol={EMOJI.trophy} /> Clear levels to unlock harder modes.
                </div>
                <div>
                    <Emoji label="Playing card" symbol={EMOJI.playingCard} /> Earn Treats by clearing Story levels and Endless boards, then spend them in the Store Treats tab.
                </div>
                <div>
                    <Emoji label="Sparkles" symbol={EMOJI.sparkles} /> Foil cards are rare special versions.
                </div>
            </div>

            <div className="tutorialActions">
                {tutorialType === "menu" && !isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderTwistedTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Twisted Mode rotates the cards.

                <div className="advancedTutorialText">
                    Play this practice round just like the real game. Pick each card
                    only once. After every correct pick, the board shuffles and each
                    card can rotate to 0°, 90°, 180°, or 270°.
                </div>
            </div>

            <div className="tutorialProgress">
                Twisted picks: {pickedIds.length}/{BASIC_PICK_COUNT}
            </div>

            <div className="tutorialBoard" aria-label="Twisted Mode tutorial cards">
                {cards.map((card) => {
                    const cardId = String(card.id);
                    const warned = warningCardId === cardId;

                    return (
                        <button
                            type="button"
                            key={card.id}
                            className={`tutorialCard tutorialCard--image ${
                                warned ? "tutorialCard--warning" : ""
                            }`}
                            onClick={() => handleTwistedCardClick(card)}
                        >
                            <img
                                src={getImgSrc(card)}
                                alt={card.name}
                                className="tutorialCardImage tutorialCardImage--advanced"
                                draggable="false"
                                style={{
                                    transform: `rotate(${card.tutorialRotation || 0}deg)`,
                                }}
                            />
                        </button>
                    );
                })}
            </div>

            <div
                className={`tutorialMessage ${
                    completed ? "tutorialMessage--success" : ""
                }`}
                role="status"
                aria-live="polite"
            >
                {message}
            </div>

            {completed && (
                <div className="tutorialCongrats">
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> You are ready for Twisted Mode!
                </div>
            )}

            <div className="tutorialTips">
                <div>
                    <Emoji label="Cyclone" symbol={EMOJI.cyclone} /> Rotations can change after every shuffle.
                </div>
                <div>
                    <Emoji label="Direct hit" symbol={EMOJI.directHit} /> All rotations are equally likely.
                </div>
                <div>
                    <Emoji label="Brain" symbol={EMOJI.brain} /> Remember the card, not just its direction.
                </div>
            </div>

            <div className="tutorialActions">
                {!isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderShyTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Shy Corgis hide temporarily.

                <div className="advancedTutorialText">
                    In Shy Corgis, cards flip face-down for a few seconds, then flip
                    back over. Each card has its own timer, and no more than half the
                    board can hide at once.
                </div>
            </div>

            {/* <div className="tutorialProgress">
                Watch the cards flip like they do during a real Shy Corgis run.
            </div> */}

            <div
                className="tutorialBoard tutorialBoard--shyDemo"
                aria-label="Shy Corgis tutorial cards"
            >
                {cards.map((card) => {
                    const hiddenByShyDemo = shyDemoHiddenIds.includes(String(card.id));

                    return (
                        <div
                            key={card.id}
                            className={`tutorialCard tutorialCard--image tutorialCard--advanced ${
                                hiddenByShyDemo ? "tutorialCard--shyHidden" : ""
                            }`}
                        >
                            <div className="tutorialCardFlipInner">
                                <div className="tutorialCardBack" aria-hidden="true" />

                                <img
                                    src={getImgSrc(card)}
                                    alt={card.name}
                                    className="tutorialCardImage tutorialCardImage--advanced"
                                    draggable="false"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="tutorialMessage" role="status" aria-live="polite">
                Remember what each card was before it hides. When it flips back, keep
                playing without picking the same card twice.
            </div>

            <div className="tutorialTips">
                <div>
                    <Emoji label="See no evil monkey" symbol={EMOJI.seeNoEvilMonkey} /> Cards hide and return automatically.
                </div>
                <div>
                    <Emoji label="Check mark" symbol={EMOJI.checkMark} /> At least half the board stays face-up.
                </div>
                <div>
                    <Emoji label="Brain" symbol={EMOJI.brain} /> The challenge is remembering cards while some are hidden.
                </div>
            </div>

            <div className="tutorialActions">
                {!isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderAnxiousTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Anxious makes the board mix itself up.

                <div className="advancedTutorialText">
                    Every 5–15 seconds the cards scramble to new positions, just like
                    after a correct pick — except no card is marked as used.
                </div>

                <div className="advancedTutorialText">
                    Cards jiggle about 2 seconds before a scramble. Keep your eye on
                    who is who.
                </div>
            </div>

            <div className="tutorialActions">
                {!isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderBlitzTutorial = () => (
        <>
            <div className="tutorialRuleBox">
                Blitz is a 2-minute challenge.

                <div className="advancedTutorialText">
                    In Endless Mode, Blitz adds a 2-minute timer to your chosen board
                    and modifiers. Your goal is to survive as long as possible.
                </div>

                <div className="advancedTutorialText">
                    Hardcore is always active in Blitz, so picking the same card twice
                    immediately ends the run. Hearts cannot be used.
                </div>

                <div className="advancedTutorialText">
                    Twisted Mode and Shy Corgis can still be toggled in Endless setup
                    before starting Blitz if those modifiers are unlocked.
                </div>
            </div>

            <div className="tutorialActions">
                {!isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}

                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderInfoTutorial = (titleLines) => (
        <>
            <div className="tutorialRuleBox">
                {titleLines.map((line) => (
                    <div key={line} className="advancedTutorialText">
                        {line}
                    </div>
                ))}
            </div>
            <div className="tutorialActions">
                {!isPage ? (
                    <button
                        type="button"
                        className="tutorialSecondaryButton"
                        onClick={goToMenu}
                    >
                        Back
                    </button>
                ) : null}
                <button
                    type="button"
                    className="homePrimaryButton"
                    onClick={handleComplete}
                >
                    {isPage ? "Done" : "Close"}
                </button>
            </div>
        </>
    );

    const renderBoostsTutorial = () =>
        renderInfoTutorial([
            "Boosts live in Inventory and can be spent during Story and Endless runs.",
            "Reveal highlights one unpicked card. Shield forgives your next mistake. Freeze keeps the board still after your next correct pick.",
            "Use as many boosts as you own on a board. Boosts are unavailable in Hardcore, Blitz, and Story Hard Mode.",
        ]);

    const renderPacksTutorial = () =>
        renderInfoTutorial([
            "Corgi Packs grant 5 random Collection cards. New cards unlock avatars; duplicates can still foil.",
            "Earn packs from Story rewards, Treats shop, or IAP Packs. Unopened packs stay in Inventory until you open them.",
            "Open packs from Profile Inventory, or buy and open them from the Store Packs tab.",
        ]);

    const renderHardModeTutorial = () =>
        renderInfoTutorial([
            "Story Hard Mode unlocks after you finish the main Story. Toggle it on the Story map to replay levels with Hardcore rules.",
            "In Hard Mode, a wrong pick ends the run immediately — hearts and boosts cannot save you.",
            "Endless Hardcore is a separate modifier on the Endless setup screen and works the same way for custom runs.",
        ]);

    if (isPage) {
        return (
            <div className="helpPage">
                <header className="helpPage__header">
                    <div className="helpPage__titles">
                        <h1 className="impactFont helpPage__title">
                            {isMenu && "Help"}
                            {isAdvancedPrompt && "New Help Unlocked"}
                            {isBasic && "Basics"}
                            {isTwisted && "Twisted Mode"}
                            {isShy && "Shy Corgis"}
                            {isAnxious && "Anxious"}
                            {isBlitz && "Blitz"}
                            {isBoosts && "Boosts"}
                            {isPacks && "Corgi Packs"}
                            {isHardMode && "Hard Mode"}
                        </h1>

                        <p className="helpPage__subtitle">
                            {isMenu &&
                                "Refresh a mechanic you already unlocked."}
                            {isAdvancedPrompt &&
                                "Review your newly unlocked modes."}
                            {isBasic && "Finish this 4-card practice round."}
                            {isTwisted && "Refresh the rotated-card modifier."}
                            {isShy && "Refresh the hiding-card modifier."}
                            {isAnxious && "Refresh the self-scrambling board."}
                            {isBlitz &&
                                "Refresh the timed Hardcore challenge."}
                            {isBoosts && "How Inventory boosts work in a run."}
                            {isPacks && "How Corgi Packs fill your Collection."}
                            {isHardMode && "Story Hard Mode and Endless Hardcore."}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="helpPage__back"
                        onClick={handlePageBack}
                    >
                        Back
                    </button>
                </header>

                <div className="helpPage__body">
                    {isMenu && renderTutorialMenu()}
                    {isAdvancedPrompt && renderAdvancedUnlockPrompt()}
                    {isBasic && renderBasicTutorial()}
                    {isTwisted && renderTwistedTutorial()}
                    {isShy && renderShyTutorial()}
                    {isAnxious && renderAnxiousTutorial()}
                    {isBlitz && renderBlitzTutorial()}
                    {isBoosts && renderBoostsTutorial()}
                    {isPacks && renderPacksTutorial()}
                    {isHardMode && renderHardModeTutorial()}
                </div>
            </div>
        );
    }

    return (
        <div className="tutorialOverlay" role="dialog" aria-modal="true">
            <div className="tutorialPanel">
                <div className="tutorialHeader">
                    <div>
                        <h2 className="impactFont tutorialTitle">
                            {isMenu && "Helpful Tips"}
                            {isAdvancedPrompt && "New Help Unlocked"}
                            {isBasic && "Basics"}
                            {isTwisted && "Twisted Mode"}
                            {isShy && "Shy Corgis"}
                            {isAnxious && "Anxious"}
                            {isBlitz && "Blitz"}
                            {isBoosts && "Boosts"}
                            {isPacks && "Corgi Packs"}
                            {isHardMode && "Hard Mode"}
                        </h2>

                        <div className="tutorialSubtitle">
                            {isMenu && "Refresh a mechanic you already unlocked."}
                            {isAdvancedPrompt && "Review your newly unlocked modes."}
                            {isBasic && "Finish this 4-card practice round."}
                            {isTwisted && "Refresh the rotated-card modifier."}
                            {isShy && "Refresh the hiding-card modifier."}
                            {isAnxious && "Refresh the self-scrambling board."}
                            {isBlitz && "Refresh the timed Hardcore challenge."}
                            {isBoosts && "How Inventory boosts work in a run."}
                            {isPacks && "How Corgi Packs fill your Collection."}
                            {isHardMode && "Story Hard Mode and Endless Hardcore."}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="tutorialCloseButton"
                        onClick={handleClose}
                        aria-label="Close tutorial"
                    >
                        ×
                    </button>
                </div>

                {isMenu && renderTutorialMenu()}
                {isAdvancedPrompt && renderAdvancedUnlockPrompt()}
                {isBasic && renderBasicTutorial()}
                {isTwisted && renderTwistedTutorial()}
                {isShy && renderShyTutorial()}
                {isAnxious && renderAnxiousTutorial()}
                {isBlitz && renderBlitzTutorial()}
                {isBoosts && renderBoostsTutorial()}
                {isPacks && renderPacksTutorial()}
                {isHardMode && renderHardModeTutorial()}
            </div>
        </div>
    );
};

export default TutorialDialog;